/**
 * useRevisionHistory Hook
 *
 * Fetches revision history for a note audit
 * Supports both provider and admin endpoints via organizationId parameter
 */

import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import { useGetAdminNoteHistoryQuery } from '../store/adminApi';
import { useGetNoteAuditHistoryQuery } from '../store/api';
import type { AuditRevisionSummary, RevisionHistoryData } from '../types';

interface UseRevisionHistoryOptions {
  /** Organization ID for admin mode (if provided, uses admin endpoint) */
  organizationId?: string;
}

interface UseRevisionHistoryResult {
  /** Revision history */
  history: RevisionHistoryData | undefined;
  /** List of revisions (sorted newest first) */
  revisions: AuditRevisionSummary[];
  /** Whether data is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
  /** Refetch function */
  refetch: () => void;
}

export const useRevisionHistory = (
  noteId: string | undefined,
  options?: UseRevisionHistoryOptions,
): UseRevisionHistoryResult => {
  const organizationId = options?.organizationId;

  // Use admin endpoint if organizationId provided, otherwise use provider endpoint
  const adminQuery = useGetAdminNoteHistoryQuery(
    organizationId && noteId ? { noteId, organizationId } : skipToken,
  );

  const providerQuery = useGetNoteAuditHistoryQuery(
    !organizationId && noteId ? { noteId: noteId } : skipToken,
    { skip: !noteId || !!organizationId },
  );

  // Use whichever query is active
  const { data, isLoading, error, refetch } = organizationId ? adminQuery : providerQuery;

  // Sort revisions by revision number (newest first)
  const revisions = useMemo(() => {
    const list = data?.revisions || [];
    return [...list].sort((a, b) => b.revision - a.revision);
  }, [data]);

  return {
    history: data,
    revisions,
    isLoading,
    error: error ? 'Failed to load revision history' : null,
    refetch,
  };
};

export default useRevisionHistory;

